import React from 'react';
import {
    Controller,
    MainMenu,
    Toolbar,
    Menu,
    Loader,
    IDynamicLoader,
    CustomArgs,
    ActionPointInfo,
} from '@tencent/workbench';
import { FIXED_KEYS_DOUBLE } from '@framework/workbench/schema/constant';
import _throttle from 'lodash/throttle';
import { TableTabFeature } from './table-tab-feature';
import Select from '@tencent/dui/esm/components/Select';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import {
    TOOLBAR_ITEMS,
    MENU_CONFIGS,
    quickConfig,
    styleConfig,
    clearConfig,
    mergeCellToolbar,
    borderStyleSelectionToolbar,
    borderWidthSelectionToolbar,
    mergeCellToolbarText,
    cancelCellToolbarText,
    mergeCellAndSelectText,
    insertTopToolbar,
    insertBottomToolbar,
    insertLeftToolbar,
    insertRightToolbar,
    tableMarginToolbarConfig,
    tableFontDirectionToolbarConfig,
    sharingColConfig,
    sharingRowConfig,
    tableFontDirectionHoriConfig,
    tableFontDirectionVertConfig,
    tableFontDirection90Config,
    tableFontDirection270Config,
    tableFontDirectionStackConfig,
    tableMarginMenuNormalConfig,
    tableMarginMenuBigConfig,
    tableMarginMenuSmallConfig,
    tableMarginMenuNoneConfig,
    deleteTableToolbar,
    deleteTableRowToolbar,
    deleteTableColToolbar,
    deleteAllTableColToolbar, cellColWidthConfig, cellRowHeightConfig,
} from './config';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { transaction } from '@src/slide/domain/slide-app';
import { BorderFeature, DEFAULT_SOLID_BORDER } from '@framework/workbench/drawer/ui/text/border/border-feature';
import MERGE_CELL from '@tencent/docs-design-resources/icon/desktop/svg/merge_cells_24.svg';
import MERGE_CELL_CANCEL from '@tencent/docs-design-resources/icon/desktop/svg/merge_cells_cancel_24.svg';
import { MenuType } from '@src/slide/domain/ui-model/constant/menu';
import { ActionType } from '@editor/src/js/constants/common-action-type';
import { wrapIdForCustomContent } from '@framework/workbench/util/wrap-id-util';
import { IGlowFeature } from '@framework/workbench/drawer/ui/text/effects/glow/glow-interface';
import { IReflectionFeature } from '@framework/workbench/drawer/ui/text/effects/reflection/reflection-interface';
import { IShadowFeature } from '@framework/workbench/drawer/ui/text/effects/shadow/shadow-interface';
import { BorderColorFeature } from '@framework/workbench/ui/border-color/border-color-feature';
import {
    AlignType,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/components/text-editor/editor-controller/editor-operations/constant';
import { ITextBoxFeature } from '@framework/workbench/drawer/ui/text/text-box/text-box-interface';
import { TextDirectionType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import { Form, NumberInputFrom } from '@tencent/wb-ext-form';
import { ChartFormItemKey } from '@tencent/chart-drawer-components/lib/schema';
import { FontStyleName } from '@framework/workbench/drawer/ui/chart/common/font/font-style/config';
import { FormItemName, INPUT_PROPS_CALLBACK_KEY } from '@framework/workbench/drawer/constants';
import { cmToPt } from '@src/slide/infrastructure/module/independent/unit';
import cloneDeep from 'lodash/cloneDeep';
import { CustomNumberInput } from '@framework/workbench/common/components/custom-number-input';
import i18n from '@i18next/i18n';
import { RowColSizeFeature } from '@framework/workbench/drawer/ui/table/row-col-size/row-col-size-feature';
import { SpinOptions } from '@framework/workbench/drawer/ui/table/row-col-size/config';
import { TDColor } from '@src/slide/domain/ui-model/element-modeles/property/color/td-color';
import { CLICK_FROM } from '@framework/workbench/ui/constant';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';

const TO_FIXED = 2;
const PRESET_CELL_PADDING = {
    [tableMarginMenuNormalConfig.id]: {
        paddingLeft: 0.25,
        paddingRight: 0.25,
        paddingTop: 0.13,
        paddingBottom: 0.13,
    },
    [tableMarginMenuBigConfig.id]: {
        paddingLeft: 0.38,
        paddingRight: 0.38,
        paddingTop: 0.38,
        paddingBottom: 0.38,
    },
    [tableMarginMenuSmallConfig.id]: {
        paddingLeft: 0.13,
        paddingRight: 0.13,
        paddingTop: 0.13,
        paddingBottom: 0.13,
    },
    [tableMarginMenuNoneConfig.id]: {
        paddingLeft: 0,
        paddingRight: 0,
        paddingTop: 0,
        paddingBottom: 0,
    },
};

@Controller({
    toolbarItems: TOOLBAR_ITEMS,
    menuItems: MENU_CONFIGS,
})
export class TableTabController {
    private throttleUpdateWidth = _throttle(this.updateWidth, 500);
    private throttleUpdateHeight = _throttle(this.updateHeight, 500);
    public constructor(
        private readonly feature: TableTabFeature,
        private readonly borderFeature: BorderFeature,
        private readonly borderColorFeature: BorderColorFeature,
        private readonly rowColFeature: RowColSizeFeature,
        @Loader(IGlowFeature) private glowLoader: IDynamicLoader<IGlowFeature>,
        @Loader(IReflectionFeature) private reflectionLoader: IDynamicLoader<IReflectionFeature>,
        @Loader(IShadowFeature) private shadowLoader: IDynamicLoader<IShadowFeature>,
        @Loader(ITextBoxFeature) private TextBoxLoader: IDynamicLoader<ITextBoxFeature>,
    ) {}

    private get textBoxFeature() {
        return this.TextBoxLoader.instance!;
    }

    // ssv的场景下需屏蔽会员功能
    @Toolbar(FIXED_KEYS_DOUBLE.TOOLBAR_TABLE, 'shouldHide')
    @Toolbar(FIXED_KEYS_DOUBLE.TOOLBAR_TABLE_POSITION, 'shouldHide')
    public shouldDoubleToolEfficiencyHide() {
        const type = this.feature.getSelectModel()?.type;
        return type !== UIModelType.TABLE;
    }

    @Toolbar(quickConfig.id, 'content')
    public getQuickConfigPanel() {
        return this.feature.getContent();
    }

    @Toolbar(styleConfig.id, 'content')
    public getStyleConfigPanel() {
        return this.feature.getCheckPanelContent();
    }

    @Toolbar(mergeCellToolbar.id, 'icon')
    public fontCellToolbarIcon() {
        const tableModel = this.feature.getSelectModel() as ITableModel;
        if (tableModel?.selectOneMergedCell?.()) {
            return <img src={MERGE_CELL_CANCEL} alt={''} />;
        }

        return <img src={MERGE_CELL} alt={''} />;
    }

    @Toolbar(mergeCellToolbar.id, 'onClick')
    @Toolbar(mergeCellToolbarText.id, 'onClick')
    @Toolbar(cancelCellToolbarText.id, 'onClick')
    @Menu(mergeCellToolbarText.id, 'onClick')
    @Menu(cancelCellToolbarText.id, 'onClick')
    public mergeCellToolbarClick() {
        this.feature.onCellOperationClick();
    }

    @Toolbar(mergeCellToolbarText.id, 'shouldDisabled')
    public mergeShouldDisabled() {
        if (!this.isEditableState()) {
            return true;
        }

        const tableModel = this.feature.getSelectModel() as ITableModel;
        const selectedRange = tableModel.selectedRange || {
            rowEnd: 0,
            rowStart: 0,
            colStart: 0,
            colEnd: 0,
        };
        const {
            rowEnd,
            rowStart,
            colStart,
            colEnd,
        } = selectedRange;

        const isJustSelectedOneCell = (rowStart - rowEnd) === 0 && (colStart - colEnd) === 0;

        return tableModel?.selectOneMergedCell?.() || isJustSelectedOneCell;
    }

    @Toolbar(mergeCellToolbar.id, 'shouldDisabled')
    public mergeCellToolbarShouldDisabled() {
        if (!this.isEditableState()) {
            return true;
        }

        const tableModel = this.feature.getSelectModel() as ITableModel;
        const selectedRange = tableModel.selectedRange || {
            rowEnd: 0,
            rowStart: 0,
            colStart: 0,
            colEnd: 0,
        };
        const {
            rowEnd,
            rowStart,
            colStart,
            colEnd,
        } = selectedRange;

        const isJustSelectedOneCell = (rowStart - rowEnd) === 0 && (colStart - colEnd) === 0;

        if (tableModel?.selectOneMergedCell?.()) {
            return false;
        }

        return isJustSelectedOneCell;
    }

    @Toolbar(cancelCellToolbarText.id, 'shouldDisabled')
    public cancelShouldDisabled() {
        if (!this.isEditableState()) {
            return true;
        }

        const tableModel = this.feature.getSelectModel() as ITableModel;
        return !tableModel?.selectOneMergedCell?.();
    }

    @Toolbar(sharingColConfig.id, 'onClick')
    public sharingColConfigOnClick() {
        this.feature.averageTableColWidth();
    }

    @Toolbar(sharingRowConfig.id, 'onClick')
    public sharingRowConfigOnClick() {
        this.feature.averageTableRowHeight();
    }

    @Menu(mergeCellAndSelectText.id, 'onClick')
    public mergeCellAndCenterMenuClick() {
        transaction(() => {
            transaction(() => {
                this.feature.onCellOperationClick();
            });
            this.feature.setCenterForMerge();
        });
    }

    @Toolbar(mergeCellToolbar.id, 'dropdownContent')
    public fontCellToolbarDropdown() {
        const tableModel = this.feature.getSelectModel() as ITableModel;
        let name = ActionType.MERGE_CELL_TOOLBAR;
        if (tableModel?.selectOneMergedCell?.()) {
            name = ActionType.CANCEL_CELL_TOOLBAR;
        }

        return <MainMenu
            align="left" name={name} mode="menu" itemStyle={{ width: 110, minWidth: 110 }} />;
    }

    @Toolbar(borderStyleSelectionToolbar.id, 'content')
    public borderStyleSelectionToolbarContent() {
        return this.borderColorFeature.getBorderStyleToolbarButton();
    }

    @Toolbar(borderStyleSelectionToolbar.id, 'dropdownContent')
    public borderStyleSelectionToolbarDropdownContent() {
        return this.borderColorFeature.getBorderMenu(ActionType.BORDER_LINE_STYLE);
    }


    @Toolbar(borderWidthSelectionToolbar.id, 'content')
    public borderWidthSelectionToolbarContent() {
        return this.borderColorFeature.getBorderWidthToolbarButton();
    }

    @Toolbar(borderWidthSelectionToolbar.id, 'dropdownContent')
    public borderWidthSelectionToolbarDropdownContent() {
        return this.borderColorFeature.getBorderMenu(FormItemName.BORDER_LINE_WIDTH);
    }

    @Toolbar(insertTopToolbar.id, 'onClick')
    public insertTopToolbarOnClick() {
        const tableModel = this.feature.getSelectModel() as ITableModel;
        tableModel.handleInsertRowTop({
            colSpan: 1,
            rowSpan: 1,
        });
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TABLE_LAYOUT_TAB,
            action: TDWActionType.TABLE_TAB_LAYOUT_INSERT_ABOVE,
        });
    }

    @Toolbar(insertBottomToolbar.id, 'onClick')
    public insertBottomToolbarOnClick() {
        const tableModel = this.feature.getSelectModel() as ITableModel;
        tableModel.handleInsertRowBottom({
            colSpan: 1,
            rowSpan: 1,
        });
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TABLE_LAYOUT_TAB,
            action: TDWActionType.TABLE_TAB_LAYOUT_INSERT_BELOW,
        });
    }

    @Toolbar(insertLeftToolbar.id, 'onClick')
    public insertLeftToolbarOnClick() {
        const tableModel = this.feature.getSelectModel() as ITableModel;
        tableModel.handleInsertColLeft({
            colSpan: 1,
            rowSpan: 1,
        });
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TABLE_LAYOUT_TAB,
            action: TDWActionType.TABLE_TAB_LAYOUT_INSERT_LEFT,
        });
    }

    @Toolbar(insertRightToolbar.id, 'onClick')
    public insertRightToolbarOnClick() {
        const tableModel = this.feature.getSelectModel() as ITableModel;
        tableModel.handleInsertColRight({
            colSpan: 1,
            rowSpan: 1,
        });
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TABLE_LAYOUT_TAB,
            action: TDWActionType.TABLE_TAB_LAYOUT_INSERT_RIGHT,
        });
    }

    @Toolbar(tableMarginToolbarConfig.id, 'dropdownContent')
    public tableMarginToolbarDropdown() {
        return <MainMenu
            align="left" name={tableMarginToolbarConfig.id} mode="menu" itemStyle={{ width: 110, minWidth: 110 }} />;
    }

    @Toolbar(tableFontDirectionToolbarConfig.id, 'dropdownContent')
    public tableFontDirectionToolbarDropdown() {
        return <MainMenu
            align="left" name={tableFontDirectionToolbarConfig.id} mode="menu" itemStyle={{ width: 166, height: 22 }} />;
    }

    @Menu(tableFontDirectionHoriConfig.id, 'shouldCheck', { key: tableFontDirectionHoriConfig.id })
    @Menu(tableFontDirectionVertConfig.id, 'shouldCheck', { key: tableFontDirectionVertConfig.id })
    @Menu(tableFontDirection90Config.id, 'shouldCheck', { key: tableFontDirection90Config.id })
    @Menu(tableFontDirection270Config.id, 'shouldCheck', { key: tableFontDirection270Config.id })
    @Menu(tableFontDirectionStackConfig.id, 'shouldCheck', { key: tableFontDirectionStackConfig.id })
    public tableFontDirectionToolbarShouldCheck(@CustomArgs() args: { key: string }) {
        const { key } = args;
        const { textDirection } = this.textBoxFeature;
        if (textDirection === TextDirectionType.WORD_ART_VERTICAL_LTR
            || textDirection === TextDirectionType.WORD_ART_VERTICAL_RTL) {
            return TextDirectionType.WORD_ART_VERTICAL_GENERAL === key;
        }
        return textDirection === key;
    }

    @Menu(tableFontDirectionHoriConfig.id, 'onClick', { key: tableFontDirectionHoriConfig.id })
    @Menu(tableFontDirectionVertConfig.id, 'onClick', { key: tableFontDirectionVertConfig.id })
    @Menu(tableFontDirection90Config.id, 'onClick', { key: tableFontDirection90Config.id })
    @Menu(tableFontDirection270Config.id, 'onClick', { key: tableFontDirection270Config.id })
    @Menu(tableFontDirectionStackConfig.id, 'onClick', { key: tableFontDirectionStackConfig.id })
    public tableFontDirectionToolbarClick(@CustomArgs() args: { key: string }) {
        const { key } = args;
        const textDirection = key === TextDirectionType.WORD_ART_VERTICAL_GENERAL
            ? TextDirectionType.WORD_ART_VERTICAL_RTL : key;
        this.textBoxFeature.updateProps(
            FormItemName.TEXT_DIRECTION, textDirection, false, false,
            { reportId: FormItemName.TEXT_DIRECTION, ver5: textDirection },
        );

        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TABLE_LAYOUT_TAB,
            action: TDWActionType.TABLE_TAB_LAYOUT_TEXT_DIRECTION,
            ver5: key,
        });
    }

    @Menu(tableMarginMenuNormalConfig.id, 'onClick', { value: tableMarginMenuNormalConfig.id })
    @Menu(tableMarginMenuBigConfig.id, 'onClick', { value: tableMarginMenuBigConfig.id })
    @Menu(tableMarginMenuSmallConfig.id, 'onClick', { value: tableMarginMenuSmallConfig.id })
    @Menu(tableMarginMenuNoneConfig.id, 'onClick', { value: tableMarginMenuNoneConfig.id })
    public tableFontPaddingClick(@CustomArgs() args: { value: number }) {
        const { value } = args;
        const padding = cloneDeep(PRESET_CELL_PADDING[value]);
        Object.keys(padding).forEach((key) => {
            padding[key] = cmToPt(padding[key], TO_FIXED);
        });
        const tableModel = this.feature.getSelectModel() as ITableModel;
        tableModel.uploadEditingCellData(true);
        tableModel.adjustCellFontPaddingWithSelectRange(padding);

        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TABLE_LAYOUT_TAB,
            action: TDWActionType.TABLE_TAB_LAYOUT_CELL_MARGINS,
            ver5: value,
        });
    }

    @Toolbar(deleteTableToolbar.id, 'dropdownContent')
    public deleteTableToolbarDropdown() {
        return <MainMenu
            align="left" name={deleteTableToolbar.id} mode="menu" itemStyle={{ width: 110, minWidth: 110 }} />;
    }

    @Menu(deleteTableRowToolbar.id, 'onClick')
    public deleteTableRowClick() {
        const tableModel = this.feature.getSelectModel() as ITableModel;
        tableModel.handleDeleteRow();
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TABLE_STYLE_TAB,
            action: TDWActionType.TABLE_TAB_STYLE_DELETE,
        });
    }

    @Menu(deleteTableColToolbar.id, 'onClick')
    public deleteTableColClick() {
        const tableModel = this.feature.getSelectModel() as ITableModel;
        tableModel.handleDeleteCol();
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TABLE_STYLE_TAB,
            action: TDWActionType.TABLE_TAB_STYLE_DELETE,
        });
    }

    @Menu(deleteAllTableColToolbar.id, 'onClick')
    public deleteAllTableClick() {
        const tableModel = this.feature.getSelectModel() as ITableModel;
        tableModel.handleDeleteTable();
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TABLE_STYLE_TAB,
            action: TDWActionType.TABLE_TAB_STYLE_DELETE,
        });
    }

    @Toolbar(cellRowHeightConfig.id, 'content')
    public cellRowHeightConfigContent() {
        const rolColHeightObj = { rolColHeight: this.rowColFeature.getTableColSize().height };
        const curValue = this.rowColFeature.getShownValue(INPUT_PROPS_CALLBACK_KEY.TABLE_ROW_COL_SIZE, rolColHeightObj);

        return <CustomNumberInput
            onChange={(value, from = NumberInputFrom.INPUT) => {
                if (from === NumberInputFrom.INPUT) {
                    this.throttleUpdateHeight(value, from);
                } else {
                    this.updateHeight(value, from);
                }
            }}
            value={curValue}
            toFixed={2}
            step={0.1}
            min={1}
            max={50}
            allowDecimal={true}
            suffix={i18n.t('厘米')}
            label={i18n.t('行高')}
        />;
    }

    @Toolbar(cellColWidthConfig.id, 'content')
    public cellColWidthConfigContent() {
        const rolColWidthObj = { rolColWidth: this.rowColFeature.getTableColSize().width };
        const curValue = this.rowColFeature.getShownValue(INPUT_PROPS_CALLBACK_KEY.TABLE_ROW_COL_SIZE, rolColWidthObj);

        return <CustomNumberInput
            onChange={(value, from = NumberInputFrom.INPUT) => {
                if (from === NumberInputFrom.INPUT) {
                    this.throttleUpdateWidth(value, from);
                } else {
                    this.updateWidth(value, from);
                }
            }}
            value={curValue}
            toFixed={2}
            step={0.1}
            min={1}
            max={50}
            allowDecimal={true}
            suffix={i18n.t('厘米')}
            label={i18n.t('列宽')}
        />;
    }

    private updateHeight(value: string, from: NumberInputFrom) {
        const number = Number(Number(value.replace(i18n.t('厘米'), '')).toFixed(2));
        const { rolColHeight } = this.rowColFeature.getValidValue({ rolColHeight: number }, from, 50);
        this.rowColFeature.updateWidthOfTableRows(rolColHeight, from);
        this.feature.toolbarService.$forceUpdate();

        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TABLE_LAYOUT_TAB,
            action: TDWActionType.TABLE_TAB_LAYOUT_ROW_SIZE,
        });
    }

    private updateWidth(value: string, from: NumberInputFrom) {
        const number = Number(Number(value.replace(i18n.t('厘米'), '')).toFixed(2));
        const { rolColWidth } = this.rowColFeature.getValidValue({ rolColWidth: number }, from, 50);
        this.rowColFeature.updateWidthOfTableCols(rolColWidth, from);
        this.feature.toolbarService.$forceUpdate();

        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.TABLE_LAYOUT_TAB,
            action: TDWActionType.TABLE_TAB_LAYOUT_COLUMN_SIZE,
        });
    }

    private isEditableState() {
        return this.feature.getUserPriviledgeService().pptEditorState < PPTEditorStateType.COPYABLE;
    }
}
